import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Trash2, FolderUp, Upload, BookPlus, Users, Music, Video, UserPlus, KeyRound, Pencil, Wand2, GripVertical, Lock, Search, Building2, Megaphone, Power, PowerOff, Download } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { extractFilesFromDataTransfer, isMediaFile } from "@/lib/dropFiles";
import { uploadFileChunked } from "@/lib/uploadChunked";

const BATCH_SIZE = 1; // 1 arquivo por requisição — evita timeout/SSL drops em vídeos grandes
const CHUNKED_UPLOAD_THRESHOLD = 20 * 1024 * 1024; // >20 MB usa upload em pedaços

export default function AdminDashboard() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#E30613] mb-2">Administração</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 tracking-tight">
            Painel Admin
          </h1>
          <p className="text-neutral-500 mt-2">Gerencie cursos, áudios/vídeos e alunos.</p>
        </div>

        <Tabs defaultValue="books" className="w-full">
          <TabsList className="rounded-sm bg-neutral-100 mb-6" data-testid="admin-tabs">
            <TabsTrigger value="books" data-testid="tab-books" className="rounded-sm">Cursos</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-sm">Alunos</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="tab-announcements" className="rounded-sm">Avisos</TabsTrigger>
          </TabsList>

          <TabsContent value="books">
            <BooksTab />
          </TabsContent>
          <TabsContent value="users">
            <UsersTab />
          </TabsContent>
          <TabsContent value="announcements">
            <AnnouncementsTab />
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}

function BooksTab() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [openNewBook, setOpenNewBook] = useState(false);
  const [renameBook, setRenameBook] = useState(null);
  const [openLTPwd, setOpenLTPwd] = useState(false);
  const [openLabels, setOpenLabels] = useState(false);
  const [labels, setLabels] = useState({ audios: "Áudios", videos: "Vídeos", listening_test: "Listening Test" });
  const [search, setSearch] = useState("");

  const filteredBooks = books.filter((b) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (b.title || "").toLowerCase().includes(q) || (b.description || "").toLowerCase().includes(q);
  });

  const loadLabels = async () => {
    try {
      const { data } = await api.get("/settings/folder-labels");
      setLabels(data);
    } catch (_) {}
  };

  const load = async () => {
    try {
      const { data } = await api.get("/books");
      setBooks(data);
      if (selectedBook) {
        const updated = data.find((b) => b.id === selectedBook.id);
        if (updated) setSelectedBook(updated);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); loadLabels(); }, []); // eslint-disable-line

  const createBook = async (title, description, category, language) => {
    try {
      await api.post("/books", { title, description, cover_url: "", category, language });
      toast.success("Curso criado");
      setOpenNewBook(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const renameBookSubmit = async (newTitle, newCategory, newLanguage) => {
    try {
      await api.patch(`/books/${renameBook.id}`, {
        title: newTitle,
        description: renameBook.description || "",
        cover_url: renameBook.cover_url || "",
        category: newCategory || renameBook.category || "audios",
        language: newLanguage || renameBook.language || "english",
      });
      toast.success("Curso atualizado");
      setRenameBook(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cycleCategory = (cat) => {
    if (cat === "audios") return "videos";
    if (cat === "videos") return "listening_test";
    return "audios";
  };

  const changeCategory = async (book) => {
    try {
      await api.patch(`/books/${book.id}/category`, { category: cycleCategory(book.category) });
      toast.success("Pasta alterada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const catBadgeClass = (cat) => {
    if (cat === "videos") return "bg-black text-white";
    if (cat === "listening_test") return "bg-amber-600 text-white";
    return "bg-[#E30613] text-white";
  };
  const catLabel = (cat, lang) => {
    const langKey = lang || "english";
    return (labels[langKey] && labels[langKey][cat]) || (cat === "videos" ? "Vídeos" : cat === "listening_test" ? "Listening" : "Áudios");
  };

  const removeBook = async (id) => {
    if (!confirm("Excluir este curso e todos os seus arquivos?")) return;
    try {
      await api.delete(`/books/${id}`);
      toast.success("Curso excluído");
      if (selectedBook?.id === id) setSelectedBook(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-[420px,1fr] gap-6">
      <div className="border border-neutral-200 rounded-sm bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50 gap-2">
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500">Cursos</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpenLabels(true)}
              data-testid="folder-labels-btn"
              className="rounded-sm"
              title="Renomear pastas"
            >
              <Pencil className="w-3.5 h-3.5 mr-1" /> Pastas
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpenLTPwd(true)}
              data-testid="lt-password-settings-btn"
              className="rounded-sm"
              title="Senha do Listening Test"
            >
              <Lock className="w-3.5 h-3.5 mr-1" /> Senha LT
            </Button>
            <Dialog open={openNewBook} onOpenChange={setOpenNewBook}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="new-book-btn" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm">
                  <BookPlus className="w-4 h-4 mr-1" /> Novo
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader><DialogTitle>Novo curso</DialogTitle></DialogHeader>
                <NewBookForm onSubmit={createBook} labels={labels} />
              </DialogContent>
            </Dialog>
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-neutral-500">Carregando...</div>
        ) : books.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500">Crie o primeiro curso.</div>
        ) : (
          <>
            <div className="px-3 py-2 border-b border-neutral-100 bg-white">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar curso..."
                  className="rounded-sm pl-8 h-9 text-sm"
                  data-testid="admin-book-search"
                />
              </div>
              {search && (
                <div className="text-xs text-neutral-500 mt-1">{filteredBooks.length} resultado(s)</div>
              )}
            </div>
            {filteredBooks.length === 0 ? (
              <div className="p-6 text-sm text-neutral-500">Nenhum curso encontrado.</div>
            ) : (
              <ul data-testid="admin-books-list">
                {filteredBooks.map((b) => (
                  <li key={b.id} className="border-b border-neutral-100">
                    <div className="flex items-stretch">
                      <button
                        data-testid={`admin-book-${b.id}`}
                        onClick={() => setSelectedBook(b)}
                        className={`flex-1 min-w-0 text-left px-3 py-3 transition-colors ${selectedBook?.id === b.id ? "bg-black text-white" : "hover:bg-neutral-50"}`}
                      >
                        <div className="font-medium text-sm flex items-center gap-2 min-w-0">
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm flex-shrink-0 ${b.language === "espanol" ? "bg-amber-600 text-white" : "bg-blue-700 text-white"}`}>
                            {b.language === "espanol" ? "ES" : "EN"}
                          </span>
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm flex-shrink-0 ${catBadgeClass(b.category)} ${selectedBook?.id === b.id ? "ring-1 ring-white/30" : ""}`}>
                            {catLabel(b.category, b.language)}
                          </span>
                          <span className="truncate">{b.title}</span>
                        </div>
                        <div className={`text-xs mt-0.5 ${selectedBook?.id === b.id ? "text-neutral-400" : "text-neutral-500"}`}>
                          {b.lessons_count} {b.lessons_count === 1 ? "arquivo" : "arquivos"}
                        </div>
                      </button>
                      <div className="flex items-center flex-shrink-0 border-l border-neutral-100">
                        <button
                          onClick={() => setRenameBook(b)}
                          data-testid={`rename-book-${b.id}`}
                          className="px-2 h-full text-neutral-400 hover:text-neutral-900 hover:bg-neutral-50 transition-colors"
                          aria-label="Renomear curso"
                          title="Renomear"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => changeCategory(b)}
                          data-testid={`toggle-category-${b.id}`}
                          className="px-2 h-full text-neutral-400 hover:text-[#E30613] hover:bg-neutral-50 transition-colors"
                          aria-label="Trocar pasta"
                          title="Trocar pasta (Áudios → Vídeos → Listening)"
                        >
                          {b.category === "audios" ? <Video className="w-4 h-4" /> : b.category === "videos" ? <Lock className="w-4 h-4" /> : <Music className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => removeBook(b.id)}
                          data-testid={`delete-book-${b.id}`}
                          className="px-2 h-full text-neutral-400 hover:text-[#E30613] hover:bg-neutral-50 transition-colors"
                          aria-label="Excluir curso"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div>
        {selectedBook ? (
          <BookManager book={selectedBook} reloadBooks={load} />
        ) : (
          <div className="border border-dashed border-neutral-300 rounded-sm p-12 text-center text-neutral-500">
            Selecione um curso à esquerda ou crie um novo.
          </div>
        )}
      </div>

      <Dialog open={!!renameBook} onOpenChange={(o) => !o && setRenameBook(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Renomear curso</DialogTitle></DialogHeader>
          {renameBook && <RenameBookForm initial={renameBook} onSubmit={renameBookSubmit} />}
        </DialogContent>
      </Dialog>

      <Dialog open={openLTPwd} onOpenChange={setOpenLTPwd}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-600" /> Senha do {labels.listening_test}
            </DialogTitle>
          </DialogHeader>
          <ListeningPasswordForm onClose={() => setOpenLTPwd(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={openLabels} onOpenChange={setOpenLabels}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Renomear pastas</DialogTitle>
          </DialogHeader>
          <FolderLabelsForm
            initial={labels}
            onSaved={(newLabels) => { setLabels(newLabels); setOpenLabels(false); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FolderLabelsForm({ initial, onSaved }) {
  const e0 = initial?.english || { audios: "Áudios", videos: "Vídeos", listening_test: "Listening Test" };
  const s0 = initial?.espanol || { audios: "Áudios", videos: "Vídeos", listening_test: "Prueba - Escucha" };
  const [enAudios, setEnAudios] = useState(e0.audios);
  const [enVideos, setEnVideos] = useState(e0.videos);
  const [enListening, setEnListening] = useState(e0.listening_test);
  const [esAudios, setEsAudios] = useState(s0.audios);
  const [esVideos, setEsVideos] = useState(s0.videos);
  const [esListening, setEsListening] = useState(s0.listening_test);

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/admin/settings/folder-labels", {
        english: {
          audios: enAudios.trim() || "Áudios",
          videos: enVideos.trim() || "Vídeos",
          listening_test: enListening.trim() || "Listening Test",
        },
        espanol: {
          audios: esAudios.trim() || "Áudios",
          videos: esVideos.trim() || "Vídeos",
          listening_test: esListening.trim() || "Prueba - Escucha",
        },
      });
      toast.success("Nomes das pastas atualizados");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <form onSubmit={save} className="space-y-4 pt-2">
      <p className="text-sm text-neutral-600">
        Personalize os nomes das pastas exibidas para os alunos em cada idioma.
      </p>
      <div className="border border-neutral-200 rounded-sm p-3 space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] text-blue-700 font-semibold">English</p>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5"><Music className="w-3.5 h-3.5 text-[#E30613]" /> Áudios</Label>
            <Input value={enAudios} onChange={(e) => setEnAudios(e.target.value)} maxLength={60} className="rounded-sm h-9 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5"><Video className="w-3.5 h-3.5" /> Vídeos</Label>
            <Input value={enVideos} onChange={(e) => setEnVideos(e.target.value)} maxLength={60} className="rounded-sm h-9 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-amber-600" /> Teste</Label>
            <Input value={enListening} onChange={(e) => setEnListening(e.target.value)} maxLength={60} className="rounded-sm h-9 text-sm" />
          </div>
        </div>
      </div>
      <div className="border border-neutral-200 rounded-sm p-3 space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] text-amber-600 font-semibold">Español</p>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5"><Music className="w-3.5 h-3.5 text-[#E30613]" /> Audios</Label>
            <Input value={esAudios} onChange={(e) => setEsAudios(e.target.value)} maxLength={60} className="rounded-sm h-9 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5"><Video className="w-3.5 h-3.5" /> Vídeos</Label>
            <Input value={esVideos} onChange={(e) => setEsVideos(e.target.value)} maxLength={60} className="rounded-sm h-9 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-amber-600" /> Prueba</Label>
            <Input value={esListening} onChange={(e) => setEsListening(e.target.value)} maxLength={60} className="rounded-sm h-9 text-sm" />
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button type="submit" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm" data-testid="save-labels-btn">Salvar</Button>
      </DialogFooter>
    </form>
  );
}

function RenameBookForm({ initial, onSubmit }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [category, setCategory] = useState(initial?.category || "audios");
  const [language, setLanguage] = useState(initial?.language || "english");
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (title.trim()) onSubmit(title.trim(), category, language); }} className="space-y-4 pt-2">
      <div className="space-y-2">
        <Label>Idioma</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { val: "english", label: "English" },
            { val: "espanol", label: "Español" },
          ].map(({ val, label }) => (
            <button
              key={val}
              type="button"
              onClick={() => setLanguage(val)}
              className={`border rounded-sm py-2 text-sm font-medium transition-colors ${language === val ? "border-[#E30613] bg-[#FFF1F2] text-[#E30613]" : "border-neutral-200 hover:bg-neutral-50 text-neutral-700"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Pasta</Label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { val: "audios", Icon: Music, label: "Áudios" },
            { val: "videos", Icon: Video, label: "Vídeos" },
            { val: "listening_test", Icon: Lock, label: "Teste" },
          ].map(({ val, Icon, label }) => (
            <button
              key={val}
              type="button"
              onClick={() => setCategory(val)}
              className={`flex flex-col items-center gap-1 border rounded-sm py-2 text-xs font-medium transition-colors ${category === val ? "border-[#E30613] bg-[#FFF1F2] text-[#E30613]" : "border-neutral-200 hover:bg-neutral-50 text-neutral-700"}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Título</Label>
        <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-sm" data-testid="rename-book-input" />
      </div>
      <DialogFooter>
        <Button type="submit" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm" data-testid="rename-book-submit">Salvar</Button>
      </DialogFooter>
    </form>
  );
}

function ListeningPasswordForm({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/listening-password");
        setCurrent(data.password || "");
        setNext(data.password || "");
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!next || next.length < 4) {
      toast.error("Mínimo 4 caracteres");
      return;
    }
    try {
      await api.patch("/admin/settings/listening-password", { password: next });
      toast.success("Senha atualizada");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <form onSubmit={save} className="space-y-4 pt-2">
      <p className="text-sm text-neutral-600">
        Esta senha é informada pelos professores para liberar o acesso à pasta "Listening Test" para os alunos. O acesso fica liberado por 12h após a digitação.
      </p>
      {loading ? (
        <div className="text-sm text-neutral-500">Carregando senha atual...</div>
      ) : (
        <>
          <div className="space-y-2">
            <Label>Senha atual</Label>
            <Input value={current} readOnly className="rounded-sm bg-neutral-50" data-testid="lt-current-pwd" />
          </div>
          <div className="space-y-2">
            <Label>Nova senha (mín. 4 caracteres)</Label>
            <Input
              type="text"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={4}
              maxLength={120}
              required
              className="rounded-sm"
              data-testid="lt-new-pwd"
            />
          </div>
          <DialogFooter>
            <Button type="submit" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm" data-testid="lt-save-pwd">
              Salvar
            </Button>
          </DialogFooter>
        </>
      )}
    </form>
  );
}

function NewBookForm({ onSubmit, labels = { english: { audios: "Áudios", videos: "Vídeos", listening_test: "Listening Test" }, espanol: { audios: "Áudios", videos: "Vídeos", listening_test: "Prueba - Escucha" } } }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [language, setLanguage] = useState("english");
  const [category, setCategory] = useState("audios");
  const safeLabels = labels[language] || { audios: "Áudios", videos: "Vídeos", listening_test: "Listening Test" };
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (title.trim()) onSubmit(title.trim(), desc.trim(), category, language); }}
      className="space-y-4 pt-2"
    >
      <div className="space-y-2">
        <Label>Idioma</Label>
        <div className="grid grid-cols-2 gap-2" data-testid="new-book-language">
          {[
            { val: "english", label: "English" },
            { val: "espanol", label: "Español" },
          ].map(({ val, label }) => (
            <button
              key={val}
              type="button"
              onClick={() => setLanguage(val)}
              data-testid={`new-book-lang-${val}`}
              className={`border rounded-sm py-2.5 text-sm font-medium transition-colors ${
                language === val
                  ? "border-[#E30613] bg-[#FFF1F2] text-[#E30613]"
                  : "border-neutral-200 hover:bg-neutral-50 text-neutral-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Pasta</Label>
        <div className="grid grid-cols-3 gap-2" data-testid="new-book-category">
          {[
            { val: "audios", Icon: Music },
            { val: "videos", Icon: Video },
            { val: "listening_test", Icon: Lock },
          ].map(({ val, Icon }) => (
            <button
              key={val}
              type="button"
              onClick={() => setCategory(val)}
              data-testid={`new-book-cat-${val}`}
              className={`flex flex-col items-center justify-center gap-1 border rounded-sm py-3 text-xs font-medium transition-colors ${
                category === val
                  ? "border-[#E30613] bg-[#FFF1F2] text-[#E30613]"
                  : "border-neutral-200 hover:bg-neutral-50 text-neutral-700"
              }`}
            >
              <Icon className="w-4 h-4" /> {safeLabels[val]}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Título</Label>
        <Input data-testid="new-book-title" required value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-sm" placeholder="Ex.: Essentials 1" />
      </div>
      <div className="space-y-2">
        <Label>Descrição (opcional)</Label>
        <Textarea data-testid="new-book-desc" value={desc} onChange={(e) => setDesc(e.target.value)} className="rounded-sm" placeholder="Descrição curta" />
      </div>
      <DialogFooter>
        <Button data-testid="new-book-submit" type="submit" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm">Criar</Button>
      </DialogFooter>
    </form>
  );
}

function BookManager({ book, reloadBooks }) {
  const [media, setMedia] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [queue, setQueue] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [dragOver, setDragOver] = useState(false);
  const [editing, setEditing] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [dragIdx, setDragIdx] = useState(null);
  const [dropIdx, setDropIdx] = useState(null);
  const [search, setSearch] = useState("");

  const filteredMedia = media.filter((m) => {
    if (!search.trim()) return true;
    return (m.title || "").toLowerCase().includes(search.trim().toLowerCase());
  });

  const loadMedia = async () => {
    setLoadingMedia(true);
    try {
      const { data } = await api.get(`/books/${book.id}/media`);
      setMedia(data);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoadingMedia(false);
    }
  };

  useEffect(() => { loadMedia(); /* eslint-disable-next-line */ }, [book.id]);

  const addToQueue = (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).filter(isMediaFile);
    if (arr.length === 0) {
      toast.error("Nenhum arquivo de áudio/vídeo encontrado.");
      return;
    }
    setQueue((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}__${f.size}`));
      const merged = [...prev];
      for (const f of arr) {
        const key = `${f.name}__${f.size}`;
        if (!seen.has(key)) { merged.push(f); seen.add(key); }
      }
      return merged;
    });
    toast.success(`${arr.length} arquivo(s) adicionado(s) à fila.`);
  };

  const onDrop = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    try {
      const files = await extractFilesFromDataTransfer(e.dataTransfer);
      addToQueue(files);
    } catch (err) {
      toast.error("Não foi possível ler a pasta arrastada.");
    }
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const removeFromQueue = (idx) => setQueue((prev) => prev.filter((_, i) => i !== idx));

  const uploadAll = async () => {
    if (queue.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: queue.length, current: "" });
    let totalOk = 0;
    let totalFail = 0;
    const failedNames = [];

    for (let i = 0; i < queue.length; i++) {
      const f = queue[i];
      setProgress({ done: i, total: queue.length, current: `${f.name} (0%)` });

      // Arquivos grandes → upload em pedaços (chunk). Pequenos → endpoint clássico.
      const useChunked = f.size >= CHUNKED_UPLOAD_THRESHOLD;
      try {
        if (useChunked) {
          await uploadFileChunked(f, book.id, (pct, phase) => {
            const label = phase === "finalizing" ? "finalizando" : phase === "uploading" ? `${pct}%` : phase;
            setProgress({ done: i, total: queue.length, current: `${f.name} (${label})` });
          });
          totalOk += 1;
        } else {
          const fd = new FormData();
          fd.append("files", f);
          const { data } = await api.post(`/books/${book.id}/media`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 300000,
          });
          const ok = Array.isArray(data) ? data.length : 0;
          totalOk += ok;
          if (ok === 0) { totalFail += 1; failedNames.push(f.name); }
        }
      } catch (err) {
        totalFail += 1;
        failedNames.push(f.name);
      }
    }

    setProgress({ done: queue.length, total: queue.length, current: "" });
    setUploading(false);
    setQueue([]);
    if (totalOk > 0) toast.success(`${totalOk} arquivo(s) enviado(s).`);
    if (totalFail > 0) {
      const preview = failedNames.slice(0, 3).join(", ");
      const extra = failedNames.length > 3 ? ` e mais ${failedNames.length - 3}` : "";
      toast.error(`${totalFail} arquivo(s) falharam: ${preview}${extra}`);
    }
    await loadMedia();
    reloadBooks();
  };

  const deleteMedia = async (id) => {
    if (!confirm("Excluir este arquivo?")) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success("Arquivo excluído");
      await loadMedia();
      reloadBooks();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const persistOrder = async (arr) => {
    try {
      await api.post(`/books/${book.id}/media/reorder`, { ids: arr.map((m) => m.id) });
    } catch (e) {
      toast.error(formatApiError(e));
      loadMedia();
    }
  };

  // Drag-and-drop entre itens
  const onItemDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const onItemDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropIdx !== idx) setDropIdx(idx);
  };
  const onItemDragEnd = () => { setDragIdx(null); setDropIdx(null); };
  const onItemDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null);
    setDropIdx(null);
    if (from === null || from === idx) return;
    const arr = [...media];
    const [moved] = arr.splice(from, 1);
    arr.splice(idx, 0, moved);
    setMedia(arr);
    persistOrder(arr);
  };

  const saveTitle = async () => {
    if (!editing || !editing.title.trim()) return;
    try {
      await api.patch(`/media/${editing.id}`, { title: editing.title.trim() });
      toast.success("Título atualizado");
      setEditing(null);
      loadMedia();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const autoRename = async (form) => {
    try {
      const { data } = await api.post(`/books/${book.id}/media/auto-rename`, form);
      toast.success(`${data.renamed} arquivo(s) renomeados.`);
      setAutoOpen(false);
      loadMedia();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Seleção
  const toggleOne = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const allSelected = media.length > 0 && selected.size === media.length;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => {
    if (allSelected || someSelected) setSelected(new Set());
    else setSelected(new Set(media.map((m) => m.id)));
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Excluir ${selected.size} arquivo(s) selecionado(s)?`)) return;
    try {
      const { data } = await api.post(`/media/bulk-delete`, { ids: Array.from(selected) });
      toast.success(`${data.deleted} arquivo(s) excluído(s).`);
      await loadMedia();
      reloadBooks();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const totalSize = queue.reduce((s, f) => s + f.size, 0);

  return (
    <div className="space-y-6">
      <div className="border border-neutral-200 rounded-sm p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-1">{book.title}</h3>
        <p className="text-sm text-neutral-500">{book.description || "Sem descrição."}</p>
        <p className="text-xs text-neutral-500 mt-2">{media.length} arquivo(s)</p>
      </div>

      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={`border-2 border-dashed rounded-sm p-6 transition-colors ${
          dragOver ? "border-[#E30613] bg-[#FFF1F2]" : "border-neutral-300 bg-neutral-50"
        }`}
        data-testid="dropzone"
      >
        <div className="flex items-center gap-2 mb-2">
          <Upload className="w-5 h-5 text-[#E30613]" />
          <h4 className="font-semibold text-neutral-900">Adicionar arquivos</h4>
        </div>
        <p className="text-sm text-neutral-600 mb-4">
          <strong>Arraste e solte</strong> uma ou várias pastas aqui — todos os áudios/vídeos serão lidos de uma vez. Ou use os botões abaixo.
        </p>

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Adicionar arquivos individuais</Label>
            <Input
              data-testid="files-input"
              type="file" multiple accept="audio/*,video/*"
              onChange={(e) => { addToQueue(e.target.files); e.target.value = ""; }}
              className="rounded-sm bg-white" disabled={uploading}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <FolderUp className="w-3.5 h-3.5" /> Adicionar uma pasta
            </Label>
            <Input
              data-testid="folder-input" type="file"
              // eslint-disable-next-line react/no-unknown-property
              webkitdirectory="" directory="" multiple
              onChange={(e) => { addToQueue(e.target.files); e.target.value = ""; }}
              className="rounded-sm bg-white" disabled={uploading}
            />
          </div>
        </div>

        {queue.length > 0 && !uploading && (
          <div className="bg-white border border-neutral-200 rounded-sm mt-4">
            <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-neutral-200">
              <div className="text-sm text-neutral-700">
                <strong>{queue.length}</strong> arquivo(s) na fila · {(totalSize / (1024 * 1024)).toFixed(1)} MB
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setQueue([])} className="rounded-sm" data-testid="clear-queue-btn">Limpar</Button>
                <Button onClick={uploadAll} className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm" data-testid="upload-btn">
                  <Upload className="w-4 h-4 mr-1" /> Enviar {queue.length}
                </Button>
              </div>
            </div>
            <ul className="max-h-48 overflow-y-auto text-sm" data-testid="upload-queue">
              {queue.map((f, idx) => (
                <li key={`${f.name}-${idx}`} className="flex items-center gap-3 px-3 py-1.5 border-b border-neutral-100 last:border-b-0">
                  <Music className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
                  <span className="flex-1 truncate text-neutral-700">{f.webkitRelativePath || f.name}</span>
                  <span className="text-xs text-neutral-500">{(f.size / (1024 * 1024)).toFixed(1)} MB</span>
                  <button onClick={() => removeFromQueue(idx)} className="text-neutral-400 hover:text-[#E30613]" aria-label="Remover">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {uploading && (
          <div className="space-y-2 bg-white border border-neutral-200 rounded-sm p-4 mt-4" data-testid="upload-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Enviando... ({progress.done}/{progress.total})</span>
              <span className="text-[#E30613] font-semibold">{pct}%</span>
            </div>
            <Progress value={pct} className="h-2" />
            {progress.current && <p className="text-xs text-neutral-500 truncate">Atual: {progress.current}</p>}
          </div>
        )}
      </div>

      <div className="border border-neutral-200 rounded-sm">
        <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Checkbox
              id="select-all"
              checked={allSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={toggleAll}
              data-testid="select-all-checkbox"
              disabled={media.length === 0}
            />
            <label htmlFor="select-all" className="text-xs uppercase tracking-[0.2em] text-neutral-500 cursor-pointer">
              {selected.size > 0 ? `${selected.size} selecionado(s)` : `Arquivos do curso (${media.length})`}
            </label>
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <Button
                size="sm"
                onClick={bulkDelete}
                className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Excluir {selected.size}
              </Button>
            )}
            <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  disabled={media.length === 0}
                  data-testid="auto-rename-btn"
                >
                  <Wand2 className="w-4 h-4 mr-1" /> Renomear IL, L1, L2…
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader><DialogTitle>Renomeação automática</DialogTitle></DialogHeader>
                <AutoRenameForm count={media.length} onSubmit={autoRename} />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loadingMedia ? (
          <div className="p-6 text-sm text-neutral-500">Carregando...</div>
        ) : media.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500">Nenhum arquivo neste curso.</div>
        ) : (
          <>
            <div className="px-3 py-2 border-b border-neutral-100 bg-white">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar arquivo..."
                  className="rounded-sm pl-8 h-9 text-sm"
                  data-testid="admin-media-search"
                />
              </div>
              {search && (
                <div className="text-xs text-neutral-500 mt-1">{filteredMedia.length} resultado(s)</div>
              )}
            </div>
            {filteredMedia.length === 0 ? (
              <div className="p-6 text-sm text-neutral-500">Nenhum arquivo encontrado.</div>
            ) : (
              <ul className="md:columns-2 md:gap-0 bg-neutral-100" data-testid="admin-media-list">
                {filteredMedia.map((m) => {
                  const idx = media.findIndex((x) => x.id === m.id);
              const isSelected = selected.has(m.id);
              const isDragging = dragIdx === idx;
              const isDropTarget = dropIdx === idx && dragIdx !== null && dragIdx !== idx;
              return (
                <li
                  key={m.id}
                  draggable
                  onDragStart={onItemDragStart(idx)}
                  onDragOver={onItemDragOver(idx)}
                  onDragEnd={onItemDragEnd}
                  onDrop={onItemDrop(idx)}
                  className={`flex items-center gap-2 px-3 py-3 bg-white break-inside-avoid border-b border-neutral-100 transition-colors ${
                    isSelected ? "bg-[#FFF1F2]" : ""
                  } ${isDragging ? "opacity-40" : ""} ${
                    isDropTarget ? "outline outline-2 outline-[#E30613] outline-offset-[-2px]" : ""
                  }`}
                  data-testid={`media-row-${m.id}`}
                >
                  <span
                    className="cursor-grab active:cursor-grabbing text-neutral-300 hover:text-neutral-700 flex-shrink-0"
                    aria-label="Arrastar para reordenar"
                    title="Arraste para reordenar"
                    data-testid={`drag-handle-${m.id}`}
                  >
                    <GripVertical className="w-4 h-4" />
                  </span>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleOne(m.id)}
                    data-testid={`select-${m.id}`}
                    aria-label={`Selecionar ${m.title}`}
                  />
                  <div className="w-9 h-9 bg-neutral-100 rounded-sm flex items-center justify-center flex-shrink-0">
                    {m.kind === "video" ? <Video className="w-4 h-4" /> : <Music className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    {editing?.id === m.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          autoFocus
                          value={editing.title}
                          onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveTitle();
                            if (e.key === "Escape") setEditing(null);
                          }}
                          className="rounded-sm h-8 text-sm"
                          data-testid={`edit-input-${m.id}`}
                        />
                        <Button size="sm" className="h-8 rounded-sm bg-[#E30613] hover:bg-[#B3040E]" onClick={saveTitle} data-testid={`save-title-${m.id}`}>OK</Button>
                        <Button size="sm" variant="ghost" className="h-8 rounded-sm" onClick={() => setEditing(null)}>X</Button>
                      </div>
                    ) : (
                      <div
                        onClick={() => setEditing({ id: m.id, title: m.title })}
                        className="cursor-text hover:bg-neutral-50 rounded-sm -mx-1 px-1"
                        data-testid={`title-${m.id}`}
                        title="Clique para editar"
                      >
                        <div className="font-medium text-sm truncate flex items-center gap-1.5">
                          {m.title}
                          <Pencil className="w-3 h-3 text-neutral-300" />
                        </div>
                        <div className="text-xs text-neutral-500">
                          {m.kind === "video" ? "Vídeo" : "Áudio"} • {(m.size / (1024 * 1024)).toFixed(1)} MB
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => deleteMedia(m.id)}
                    data-testid={`delete-media-${m.id}`}
                    className="p-1.5 text-neutral-400 hover:text-[#E30613] rounded-sm hover:bg-neutral-100 flex-shrink-0"
                    aria-label="Excluir arquivo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AutoRenameForm({ count, onSubmit }) {
  const [prefix, setPrefix] = useState("L");
  const [by, setBy] = useState("name");
  const [useIL, setUseIL] = useState(true);
  const [introLabel, setIntroLabel] = useState("IL");

  const preview = () => {
    if (count === 0) return "";
    const total = useIL ? count - 1 : count;
    const width = Math.max(2, String(Math.max(total, 1)).length);
    const items = [];
    if (useIL) items.push(introLabel || "IL");
    for (let i = 0; i < Math.min(3, count - (useIL ? 1 : 0)); i++) {
      items.push(`${prefix || "L"}${String(i + 1).padStart(width, "0")}`);
    }
    return items.join(", ") + (count > items.length ? "…" : "");
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          prefix: prefix.trim() || "L",
          by,
          intro_label: useIL ? (introLabel.trim() || "IL") : "",
        });
      }}
      className="space-y-4 pt-2"
    >
      <p className="text-sm text-neutral-600">
        Renomeia os <strong>{count}</strong> arquivos do curso. Preview:&nbsp;
        <code className="bg-neutral-100 px-1.5 py-0.5 rounded-sm">{preview()}</code>
      </p>

      <div className="flex items-start gap-3 bg-neutral-50 border border-neutral-200 rounded-sm p-3">
        <Checkbox
          id="use-il"
          checked={useIL}
          onCheckedChange={(v) => setUseIL(!!v)}
          data-testid="use-il-checkbox"
        />
        <div className="flex-1 space-y-2">
          <label htmlFor="use-il" className="text-sm font-medium cursor-pointer">
            Primeira faixa como "IL" (Into Lesson)
          </label>
          {useIL && (
            <Input
              value={introLabel}
              onChange={(e) => setIntroLabel(e.target.value)}
              className="rounded-sm h-8 text-sm max-w-[160px]"
              maxLength={6}
              data-testid="il-label-input"
            />
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Prefixo das demais faixas</Label>
        <Input
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
          className="rounded-sm"
          maxLength={6}
          data-testid="auto-rename-prefix"
        />
      </div>

      <div className="space-y-2">
        <Label>Ordem</Label>
        <div className="flex flex-col gap-1.5 text-sm">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="radio" name="by" value="name" checked={by === "name"} onChange={() => setBy("name")} data-testid="auto-by-name" />
            <span>Pelo nome original do arquivo (recomendado)</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="radio" name="by" value="current" checked={by === "current"} onChange={() => setBy("current")} data-testid="auto-by-current" />
            <span>Pela ordem atual da lista</span>
          </label>
        </div>
      </div>

      <DialogFooter>
        <Button type="submit" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm" data-testid="auto-rename-submit">
          Renomear
        </Button>
      </DialogFooter>
    </form>
  );
}
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [openMyPwd, setOpenMyPwd] = useState(false);
  const [pwdUser, setPwdUser] = useState(null);
  const [unitUser, setUnitUser] = useState(null);
  const [unitFilter, setUnitFilter] = useState("all"); // all | sacoma | pauliceia | none

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredUsers = useMemo(() => {
    if (unitFilter === "all") return users;
    if (unitFilter === "none") return users.filter((u) => u.role !== "admin" && !u.unit);
    return users.filter((u) => u.unit === unitFilter);
  }, [users, unitFilter]);

  const unitCounts = useMemo(() => {
    const students = users.filter((u) => u.role !== "admin");
    return {
      all: users.length,
      sacoma: students.filter((u) => u.unit === "sacoma").length,
      pauliceia: students.filter((u) => u.unit === "pauliceia").length,
      none: students.filter((u) => !u.unit).length,
    };
  }, [users]);

  const removeUser = async (u) => {
    if (!confirm(`Excluir o aluno "${u.name}" (${u.email})?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Aluno excluído");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createUser = async (form) => {
    try {
      await api.post("/admin/users", form);
      toast.success("Aluno criado");
      setOpenCreate(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeUserPwd = async (newPwd) => {
    try {
      await api.patch(`/admin/users/${pwdUser.id}/password`, { password: newPwd });
      toast.success("Senha atualizada");
      setPwdUser(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCsv = async () => {
    const unitParam = unitFilter && unitFilter !== "all" && unitFilter !== "none" ? `?unit=${unitFilter}` : "";
    try {
      const res = await api.get(`/admin/users/export.csv${unitParam}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = unitFilter && unitFilter !== "all" && unitFilter !== "none" ? unitFilter : "todos";
      a.download = `alunos_fisk_${suffix}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV baixado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeUserUnit = async (newUnit) => {
    try {
      await api.patch(`/admin/users/${unitUser.id}/unit`, { unit: newUnit });
      toast.success("Unidade atualizada");
      setUnitUser(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeMyPwd = async (current, next) => {
    try {
      await api.patch("/auth/password", { current_password: current, new_password: next });
      toast.success("Sua senha foi atualizada");
      setOpenMyPwd(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <div className="text-sm text-neutral-600">
          {filteredUsers.length} de {users.length} usuário(s)
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={exportCsv}
            data-testid="export-users-csv-btn"
            title="Exportar alunos filtrados em CSV"
          >
            <Download className="w-4 h-4 mr-1" /> Exportar CSV
          </Button>
          <Dialog open={openMyPwd} onOpenChange={setOpenMyPwd}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-sm" data-testid="change-my-password-btn">
                Trocar minha senha
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>Trocar minha senha</DialogTitle></DialogHeader>
              <MyPasswordForm onSubmit={changeMyPwd} />
            </DialogContent>
          </Dialog>

          <Dialog open={openCreate} onOpenChange={setOpenCreate}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm" data-testid="new-user-btn">
                <UserPlus className="w-4 h-4 mr-1" /> Novo aluno
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>Cadastrar aluno manualmente</DialogTitle></DialogHeader>
              <NewUserForm onSubmit={createUser} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filtro por unidade */}
      <div className="flex flex-wrap items-center gap-2 border border-neutral-200 rounded-sm p-2 bg-neutral-50" data-testid="unit-filter">
        <Building2 className="w-4 h-4 text-neutral-500 ml-1" />
        <UnitFilterBtn active={unitFilter === "all"} onClick={() => setUnitFilter("all")} testId="filter-all">
          Todas ({unitCounts.all})
        </UnitFilterBtn>
        <UnitFilterBtn active={unitFilter === "sacoma"} onClick={() => setUnitFilter("sacoma")} testId="filter-sacoma">
          Sacomã ({unitCounts.sacoma})
        </UnitFilterBtn>
        <UnitFilterBtn active={unitFilter === "pauliceia"} onClick={() => setUnitFilter("pauliceia")} testId="filter-pauliceia">
          Paulicéia ({unitCounts.pauliceia})
        </UnitFilterBtn>
        {unitCounts.none > 0 && (
          <UnitFilterBtn active={unitFilter === "none"} onClick={() => setUnitFilter("none")} testId="filter-none">
            Sem unidade ({unitCounts.none})
          </UnitFilterBtn>
        )}
      </div>

      <div className="border border-neutral-200 rounded-sm">
        <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50 flex items-center gap-2">
          <Users className="w-4 h-4 text-neutral-500" />
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500">Alunos cadastrados</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-neutral-500">Carregando...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500">Nenhum usuário nesta unidade.</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-white text-left">
              <tr className="border-b border-neutral-200 text-xs uppercase tracking-[0.2em] text-neutral-500">
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">E-mail</th>
                <th className="px-4 py-3">Unidade</th>
                <th className="px-4 py-3">Papel</th>
                <th className="px-4 py-3">Cadastro</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id} className="border-b border-neutral-100" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium">{u.name}</td>
                  <td className="px-4 py-3 text-neutral-700">{u.email}</td>
                  <td className="px-4 py-3">
                    {u.role === "admin" ? (
                      <span className="text-xs text-neutral-400">—</span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        {u.unit ? (
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm font-medium ${u.unit === "sacoma" ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-orange-50 text-orange-700 border border-orange-200"}`} data-testid={`user-unit-${u.id}`}>
                            <Building2 className="w-3 h-3" />
                            {u.unit === "sacoma" ? "Sacomã" : "Paulicéia"}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm">Não definida</span>
                        )}
                        <button
                          onClick={() => setUnitUser(u)}
                          data-testid={`edit-unit-${u.id}`}
                          className="p-1 text-neutral-400 hover:text-[#E30613] rounded-sm hover:bg-neutral-100 transition-colors"
                          aria-label={`Editar unidade de ${u.name}`}
                          title="Editar unidade"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-sm ${u.role === "admin" ? "bg-[#E30613] text-white" : "bg-neutral-100 text-neutral-700"}`}>
                      {u.role === "admin" ? "Admin" : "Aluno"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-500 text-xs">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("pt-BR") : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {u.role === "admin" ? (
                      <span className="text-xs text-neutral-400">—</span>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`reset-pwd-${u.id}`}
                          onClick={() => setPwdUser(u)}
                          className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900 transition-colors px-2 py-1 rounded-sm hover:bg-neutral-50"
                          aria-label={`Trocar senha de ${u.name}`}
                        >
                          <KeyRound className="w-3.5 h-3.5" /> Senha
                        </button>
                        <button
                          data-testid={`delete-user-${u.id}`}
                          onClick={() => removeUser(u)}
                          className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#E30613] transition-colors px-2 py-1 rounded-sm hover:bg-neutral-50"
                          aria-label={`Excluir ${u.name}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Excluir
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!pwdUser} onOpenChange={(o) => !o && setPwdUser(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Trocar senha de {pwdUser?.name}</DialogTitle>
          </DialogHeader>
          {pwdUser && <ResetPasswordForm onSubmit={changeUserPwd} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!unitUser} onOpenChange={(o) => !o && setUnitUser(null)}>
        <DialogContent className="rounded-sm" data-testid="edit-unit-dialog">
          <DialogHeader>
            <DialogTitle>Editar unidade de {unitUser?.name}</DialogTitle>
          </DialogHeader>
          {unitUser && <EditUnitForm current={unitUser.unit} onSubmit={changeUserUnit} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditUnitForm({ current, onSubmit }) {
  const [unit, setUnit] = useState(current || "");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (!unit) return toast.error("Selecione a unidade"); onSubmit(unit); }}
      className="space-y-4 pt-2"
    >
      <div className="space-y-2">
        <Label>Unidade</Label>
        <Select value={unit} onValueChange={setUnit}>
          <SelectTrigger data-testid="edit-unit-select" className="rounded-sm">
            <SelectValue placeholder="Selecione a unidade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sacoma">FISK Sacomã (SP)</SelectItem>
            <SelectItem value="pauliceia">FISK Paulicéia (SBC)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button data-testid="edit-unit-submit" type="submit" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm">Salvar</Button>
      </DialogFooter>
    </form>
  );
}

function NewUserForm({ onSubmit }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [unit, setUnit] = useState("");
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (password.length < 6) return toast.error("Senha mín. 6 caracteres");
        if (!unit) return toast.error("Selecione a unidade");
        onSubmit({ name, email, password, unit });
      }}
      className="space-y-4 pt-2"
      data-testid="new-user-form"
    >
      <div className="space-y-2">
        <Label>Nome</Label>
        <Input data-testid="new-user-name" required value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>E-mail</Label>
        <Input data-testid="new-user-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>Unidade</Label>
        <Select value={unit} onValueChange={setUnit}>
          <SelectTrigger data-testid="new-user-unit" className="rounded-sm">
            <SelectValue placeholder="Selecione a unidade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sacoma">FISK Sacomã (SP)</SelectItem>
            <SelectItem value="pauliceia">FISK Paulicéia (SBC)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Senha inicial (mín. 6)</Label>
        <Input data-testid="new-user-password" type="text" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-sm" placeholder="Pode ser alterada depois" />
      </div>
      <DialogFooter>
        <Button data-testid="new-user-submit" type="submit" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm">Cadastrar</Button>
      </DialogFooter>
    </form>
  );
}

function UnitFilterBtn({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-xs px-3 py-1.5 rounded-sm font-medium transition-colors ${active ? "bg-[#E30613] text-white" : "bg-white text-neutral-700 hover:bg-neutral-100 border border-neutral-200"}`}
    >
      {children}
    </button>
  );
}

function ResetPasswordForm({ onSubmit }) {
  const [pwd, setPwd] = useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (pwd.length < 6) return toast.error("Senha mín. 6 caracteres"); onSubmit(pwd); }}
      className="space-y-4 pt-2"
    >
      <div className="space-y-2">
        <Label>Nova senha</Label>
        <Input data-testid="reset-pwd-input" type="text" required minLength={6} value={pwd} onChange={(e) => setPwd(e.target.value)} className="rounded-sm" />
      </div>
      <DialogFooter>
        <Button data-testid="reset-pwd-submit" type="submit" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm">Atualizar senha</Button>
      </DialogFooter>
    </form>
  );
}

function MyPasswordForm({ onSubmit }) {
  const [curr, setCurr] = useState("");
  const [next, setNext] = useState("");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (next.length < 6) return toast.error("Nova senha mín. 6 caracteres"); onSubmit(curr, next); }}
      className="space-y-4 pt-2"
    >
      <div className="space-y-2">
        <Label>Senha atual</Label>
        <Input data-testid="my-pwd-current" type="password" required value={curr} onChange={(e) => setCurr(e.target.value)} className="rounded-sm" />
      </div>
      <div className="space-y-2">
        <Label>Nova senha (mín. 6)</Label>
        <Input data-testid="my-pwd-new" type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)} className="rounded-sm" />
      </div>
      <DialogFooter>
        <Button data-testid="my-pwd-submit" type="submit" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm">Atualizar</Button>
      </DialogFooter>
    </form>
  );
}

function AnnouncementsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/announcements");
      setList(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (form) => {
    try {
      await api.post("/admin/announcements", form);
      toast.success("Aviso publicado");
      setOpenNew(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/admin/announcements/${a.id}`, { active: !a.active });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (a) => {
    if (!confirm("Excluir este aviso?")) return;
    try {
      await api.delete(`/admin/announcements/${a.id}`);
      toast.success("Aviso excluído");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async (form) => {
    try {
      await api.patch(`/admin/announcements/${editing.id}`, form);
      toast.success("Aviso atualizado");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const targetLabel = (t) => t === "sacoma" ? "Unid. Sacomã" : t === "pauliceia" ? "Unid. Paulicéia" : "Todas as unidades";

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <div className="text-sm text-neutral-600">
          {list.length} aviso(s) — {list.filter((a) => a.active).length} ativo(s)
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm" data-testid="new-announcement-btn">
              <Megaphone className="w-4 h-4 mr-1" /> Novo aviso
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm" data-testid="new-announcement-dialog">
            <DialogHeader><DialogTitle>Publicar novo aviso</DialogTitle></DialogHeader>
            <AnnouncementForm onSubmit={create} submitLabel="Publicar" />
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-neutral-200 rounded-sm">
        <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50 flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-neutral-500" />
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500">Avisos publicados</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-neutral-500">Carregando...</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-sm text-neutral-500 text-center" data-testid="no-announcements">
            Nenhum aviso publicado ainda. Clique em "Novo aviso" para criar o primeiro.
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100" data-testid="announcements-admin-list">
            {list.map((a) => (
              <li key={a.id} data-testid={`announcement-row-${a.id}`} className="p-4 flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${a.active ? "bg-emerald-500" : "bg-neutral-300"}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-neutral-900 whitespace-pre-wrap break-words">{a.text}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                    <span className="px-2 py-0.5 bg-neutral-100 rounded-sm">{targetLabel(a.target)}</span>
                    <span>{a.created_at ? new Date(a.created_at).toLocaleString("pt-BR") : ""}</span>
                    {!a.active && <span className="text-neutral-400">· inativo</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => toggleActive(a)}
                    data-testid={`toggle-announcement-${a.id}`}
                    className={`p-1.5 rounded-sm hover:bg-neutral-100 transition-colors ${a.active ? "text-emerald-600" : "text-neutral-400"}`}
                    title={a.active ? "Desativar" : "Ativar"}
                    aria-label={a.active ? "Desativar aviso" : "Ativar aviso"}
                  >
                    {a.active ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => setEditing(a)}
                    data-testid={`edit-announcement-${a.id}`}
                    className="p-1.5 rounded-sm hover:bg-neutral-100 text-neutral-500 hover:text-[#E30613] transition-colors"
                    aria-label="Editar aviso"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(a)}
                    data-testid={`delete-announcement-${a.id}`}
                    className="p-1.5 rounded-sm hover:bg-neutral-100 text-neutral-500 hover:text-[#E30613] transition-colors"
                    aria-label="Excluir aviso"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm" data-testid="edit-announcement-dialog">
          <DialogHeader><DialogTitle>Editar aviso</DialogTitle></DialogHeader>
          {editing && <AnnouncementForm initial={editing} onSubmit={saveEdit} submitLabel="Salvar" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AnnouncementForm({ initial, onSubmit, submitLabel = "Salvar" }) {
  const [text, setText] = useState(initial?.text || "");
  const [target, setTarget] = useState(initial?.target || "all");
  const [active, setActive] = useState(initial ? initial.active : true);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const t = text.trim();
        if (!t) return toast.error("Escreva o texto do aviso");
        onSubmit({ text: t, target, active });
      }}
      className="space-y-4 pt-2"
      data-testid="announcement-form"
    >
      <div className="space-y-2">
        <Label>Texto do aviso</Label>
        <Textarea
          data-testid="announcement-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Ex.: Prova oral na próxima quinta às 19h. Levar o curso."
          className="rounded-sm min-h-[110px]"
          required
        />
      </div>
      <div className="space-y-2">
        <Label>Destinatário</Label>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger data-testid="announcement-target" className="rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as unidades</SelectItem>
            <SelectItem value="sacoma">FISK Sacomã (SP)</SelectItem>
            <SelectItem value="pauliceia">FISK Paulicéia (SBC)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between border border-neutral-200 rounded-sm px-3 py-2">
        <div>
          <div className="text-sm font-medium">Aviso ativo</div>
          <div className="text-xs text-neutral-500">Se desligar, o aluno não verá.</div>
        </div>
        <Switch checked={active} onCheckedChange={setActive} data-testid="announcement-active" />
      </div>
      <DialogFooter>
        <Button type="submit" data-testid="announcement-submit" className="bg-[#E30613] hover:bg-[#B3040E] rounded-sm">{submitLabel}</Button>
      </DialogFooter>
    </form>
  );
}

